/**
 * SearchAgendaCell — the per-row AgendaGPT action: the My Searches table's
 * AgendaGPT column and, since 2026-10-07, the right-hand action of each row of
 * the home page's My open searches widget (Lauren via Jared). Lives under
 * `features/agenda/` (moved from `routes/client-engagement/searches/` that day)
 * so the dashboard widget can reuse it without importing the
 * client-engagement feature — the boundary that widget keeps.
 *
 * One icon button per row that asks the n8n AgendaGPT workflow for a meeting
 * agenda for that search (Jared, 2026-08-27: "if we can add one more column
 * that is agenda GPT button to create the agenda").
 *
 * The SAME action the Search Detail header offers, so it shares the same
 * implementation (`features/agenda/submitAgendaForm`) rather than a second copy
 * of it — every property of that POST was paid for by a debugging session
 * against a workflow that answered 200 while dropping the submission.
 *
 * What is deliberately different here is the SHAPE, not the behaviour. The
 * detail page has room for a labelled button; a table row does not, and the row
 * must not grow taller, so this is an icon with the label in `aria-label` and
 * `title`. The icon is `Mail` — an envelope, since the agenda arrives by email
 * (Lauren via Jared, 2026-10-07; it was `ListChecks`, a checklist, until then).
 *
 * Fire-and-forget, like the detail page's: the workflow emails the agenda, so
 * there is nothing to navigate to or poll for, and the button is its own retry.
 * There is no confirmation step — the request is cheap, reversible by ignoring
 * an email, and carries no input the reader could get wrong (the search comes
 * from the row, the address from the session).
 *
 * WHY `useState` AND NOT `useMutation`, unlike the Search Detail button. There
 * is no server state here: no cache key, no invalidation, nothing to read back,
 * and no retry policy — the POST leaves and a toast reports it. TanStack would
 * buy only the `isPending` flag, and it would charge a `QueryClientProvider`
 * for it at EVERY render site of `SearchesTable`, which is rendered bare in its
 * unit tests. That was measured, not assumed: wiring `useMutation` here failed
 * all 25 of those tests with "No QueryClient set". The shared
 * `submitAgendaForm` + `agendaErrorMessage` are what actually keep the two
 * call sites behaving identically, and those are still shared.
 *
 * Disabled without an authenticated session because the email IS the output
 * channel: submitting an empty one produces a workflow run that succeeds and
 * reaches nobody.
 *
 * Design-system token utilities only; English copy only.
 */
import { Mail } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  type AgendaSubmissionError,
  agendaErrorMessage,
  submitAgendaForm,
} from "./submitAgendaForm";

export interface SearchAgendaCellProps {
  searchId: string;
  /** Named in the toast and the accessible label so a row's action is specific. */
  searchName: string;
  /** The caller's address — the agenda is mailed to it. Empty disables the button. */
  meEmail: string;
  /** Defaults to the table's `searches-row-agenda-gpt`. */
  testId?: string;
}

export function SearchAgendaCell({
  searchId,
  searchName,
  meEmail,
  testId = "searches-row-agenda-gpt",
}: SearchAgendaCellProps) {
  const [pending, setPending] = useState(false);

  async function request() {
    setPending(true);
    try {
      await submitAgendaForm({ searchId, email: meEmail });
      // Names the search: on a 25-row table the reader has just pressed one of
      // 25 identical icons, and "Agenda request submitted" alone does not say
      // which one answered.
      toast.success(`Agenda request submitted for ${searchName}`);
    } catch (error) {
      toast.error(agendaErrorMessage(error as AgendaSubmissionError));
    } finally {
      // In `finally`, so a rejected submit re-arms the button. The button IS
      // the retry — leaving it disabled after a failure would strand the row
      // with a toast telling the reader to try again and no way to.
      setPending(false);
    }
  }

  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={`Create an AgendaGPT meeting agenda for ${searchName}`}
      title={
        meEmail
          ? `Create an AgendaGPT meeting agenda for ${searchName}`
          : "Sign in again to request an agenda — we need your email to send it to."
      }
      disabled={!meEmail || pending}
      // Both the click AND the key event are stopped: the row opens the Search
      // Detail on Enter/Space, so without the keydown guard one Enter on this
      // button would fire the request AND navigate away from the toast that
      // reports it.
      onClick={(e) => {
        e.stopPropagation();
        void request();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
      }}
      className="inline-flex items-center justify-center rounded-md p-1.5 text-text-secondary hover:bg-accent-muted hover:text-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:cursor-not-allowed disabled:text-text-muted disabled:hover:bg-transparent"
    >
      <Mail className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}
